import Button from "@/components/Button";
import useModal from "@/hooks/useModal";
import { useMutation, useQuery } from "@apollo/client";
import classNames from "classnames";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { UPSERT_RECRUITMENT_LEAD } from "../mutations";
import { LEAD_REFERRAL_SOURCES_QUERY } from "../queries";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import { showSnackbar, showErrorSnackbar } from "@/lib/snackbarUtils";

interface DefaultVariables {
  id?: string | null;
  personId: string | null;
  zipcode?: string | null;
  referralSource?: string | null;
}

interface FormValues {
  id?: string | null;
  personId: string | null;
  zipcode?: string | null;
  referralSource: string;
}

export default function UpsertLeadModal({
  onClosed,
  variables,
  isUpdate = false
}: {
  onClosed: () => void;
  variables: DefaultVariables;
  isUpdate: boolean;
}) {
  const { Modal, close } = useModal();

  // Fetch referral source options from backend.
  const { data: referralResponse } = useQuery(LEAD_REFERRAL_SOURCES_QUERY);

  const [upsertLead] = useMutation(UPSERT_RECRUITMENT_LEAD);

  const form = useForm<FormValues>({
    mode: "onBlur",
    defaultValues: {
      id: variables.id || null,
      personId: variables.personId || "",
      zipcode: variables.zipcode || null,
      referralSource: variables.referralSource || ""
    }
  });
  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting }
  } = form;

  const onSubmit = async (values: FormValues) => {
    const { data } = await upsertLead({
      variables: {
        input: {
          id: values.id,
          personId: values.personId,
          zipcode: values.zipcode,
          referralSource: values.referralSource
        }
      }
    });

    if (data?.upsertRecruitmentLead?.success) {
      showSnackbar(data.upsertRecruitmentLead?.message || (isUpdate ? "Lead updated" : "Lead created"));
      close();
    } else {
      showErrorSnackbar(data?.upsertRecruitmentLead?.message || "Unable to save lead");
    }
  };

  return (
    <Modal
      headline={`${isUpdate ? "Edit" : "Create"} Lead`}
      data-testid="create-lead-modal"
      onClosed={onClosed}
      className="w-[490px] p-6 pr-2 h-fit"
      displayOverflow
      action={
        <Button
          disabled={!isValid || isSubmitting}
          onClick={handleSubmit(onSubmit)}
          iconClassName={classNames({ "[&>md-icon]:animate-spin": isSubmitting })}
          icon={isSubmitting ? "progress_activity" : "check"}
        >
          {isSubmitting ? "Saving" : "Save"}
        </Button>
      }
    >
      <FormProvider {...form}>
        <div className="space-y-6">
          <Controller
            control={control}
            name="referralSource"
            rules={{ required: "Referral source is required." }}
            render={({ field }) => (
              <Select
                label="Referral Source"
                required
                options={
                  referralResponse?.referralSourceOptions?.enumValues?.map((enumValue: any) => ({
                    value: enumValue.description,
                    displayText: enumValue.description
                  })) || []
                }
                value={field.value}
                onSelect={(value) => field.onChange(value)}
                optionListMaxHeight="extended"
                className="w-full"
              />
            )}
          />
          <Controller
            name="zipcode"
            control={control}
            render={({ field }) => (
              <TextField
                label="Zipcode"
                value={field.value || undefined}
                onChange={(e) => field.onChange(e)}
                className="w-full"
              />
            )}
          />
        </div>
      </FormProvider>
    </Modal>
  );
}
